import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { Button } from '../components/ui/button';
import { RefreshCw, TrendingUp, Clock } from 'lucide-react';

export default function RecurringPatternsPage() {
  const { activePeriod } = usePeriod();
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = activePeriod ? { period_id: activePeriod.id } : {};
      const res = await API.get('/recurring-patterns', { params });
      setPatterns(res.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activePeriod?.id]);

  const formatCurrency = (n) => n?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }) || '—';

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={20} className="text-violet-600" />
          <div>
            <h1 className="text-xl font-bold">Recurring Patterns</h1>
            <p className="text-sm text-slate-500">Auto-detected recurring payments from transaction history</p>
          </div>
        </div>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><RefreshCw size={24} className="animate-spin text-slate-400" /></div>
      ) : patterns.length === 0 ? (
        <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
          <TrendingUp size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No recurring patterns detected yet.</p>
          <p className="text-xs text-slate-400 mt-1">Patterns are detected when merchants appear multiple times with similar amounts and regular intervals.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {patterns.map((p, i) => (
            <div key={i} className="bg-white rounded-lg border shadow-sm p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-sm">{p.merchant_display}</div>
                  <div className="font-mono text-xs text-slate-400">{p.merchant_normalized}</div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white
                  ${p.confidence >= 0.8 ? 'bg-emerald-500' : p.confidence >= 0.65 ? 'bg-amber-500' : 'bg-orange-500'}`}>
                  {Math.round(p.confidence * 100)}%
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-400">Avg Amount</div>
                  <div className="font-semibold tabular-nums">{formatCurrency(p.avg_amount)}</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-400">Occurrences</div>
                  <div className="font-semibold">{p.occurrence_count}x</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-400">Avg Cadence</div>
                  <div className="font-semibold flex items-center gap-1"><Clock size={10} />{p.avg_cadence_days}d</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-400">Last Seen</div>
                  <div className="font-semibold">{p.last_date}</div>
                </div>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">{p.explanation}</p>

              {p.last_category_name && (
                <div className="text-xs border-t pt-2">
                  <span className="text-slate-400">Last coding: </span>
                  <span className="font-medium">{p.last_category_name}</span>
                  {p.last_cost_center_name && <span className="text-slate-400"> / {p.last_cost_center_name}</span>}
                </div>
              )}

              <div className="flex gap-2 text-[10px]">
                {p.is_regular_cadence && <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">Regular cadence</span>}
                {p.similar_amounts && <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-700 font-semibold">Stable amount</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
